import { Checkbox, useToast } from "@design";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { z } from "zod";
import { type ApiKeyCreatedResponse, createApiKeyMutation, errorMessage, listApiKeysQueryKey, schemas } from "@/api";
import { useAppForm } from "@/components/form/form";
import { Text } from "@/components/Text";
import { type ApiKeyScope, useAvailableScopes } from "./apiKeyScopes";

const request = schemas.zCreateApiKeyRequest.shape;

function ScopeChecklist({
  scopes,
  value,
  onChange,
}: Readonly<{ scopes: ApiKeyScope[]; value: ApiKeyScope[]; onChange: (next: ApiKeyScope[]) => void }>) {
  const { t } = useTranslation("settings");
  return (
    <fieldset className="flex flex-col gap-3">
      <legend className="mb-1">
        <Text as="span" variant="body-sm">
          {t("security.form.scopes")}
        </Text>
      </legend>
      {scopes.map((scope) => (
        <Checkbox
          key={scope}
          label={t(`security.scopes.${scope}.label`)}
          description={t(`security.scopes.${scope}.description`)}
          checked={value.includes(scope)}
          onChange={(checked) => onChange(checked ? [...value, scope] : value.filter((s) => s !== scope))}
        />
      ))}
    </fieldset>
  );
}

export function NewApiKeyForm({ onCreated }: Readonly<{ onCreated: (key: ApiKeyCreatedResponse) => void }>) {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const scopes = useAvailableScopes();
  const create = useMutation({ ...createApiKeyMutation(), gcTime: 0 });
  const schema = z.object({
    name: request.name,
    scopes: z.array(z.enum(scopes)).min(1, t("security.form.scopesRequired")),
  });

  const form = useAppForm({
    defaultValues: { name: "", scopes: ["completions"] } as z.infer<typeof schema>,
    validators: { onChange: schema },
    onSubmit: async ({ value }) => {
      try {
        const created = await create.mutateAsync({ body: { name: value.name.trim(), scopes: value.scopes } });
        await queryClient.invalidateQueries({ queryKey: listApiKeysQueryKey() });
        onCreated(created);
      } catch (error) {
        toast.error(errorMessage(error, t("security.form.createFailed")));
      }
    },
  });

  return (
    <form
      aria-label={t("security.form.label")}
      className="flex flex-col gap-5"
      onSubmit={(e) => {
        e.preventDefault();
        void form.handleSubmit();
      }}
    >
      <form.AppField name="name">
        {(field) => (
          <field.TextField label={t("security.form.name")} required placeholder={t("security.form.namePlaceholder")} />
        )}
      </form.AppField>
      <form.Field name="scopes">
        {(field) => (
          <div className="flex flex-col gap-1">
            <ScopeChecklist scopes={scopes} value={field.state.value} onChange={field.handleChange} />
            {field.state.meta.errors.length > 0 && (
              <Text variant="caption" tone="error" role="alert">
                {t("security.form.scopesRequired")}
              </Text>
            )}
          </div>
        )}
      </form.Field>
      <form.AppForm>
        <form.SubmitActions
          onCancel={() => navigate("/settings/security")}
          submitLabel={t("security.form.submit")}
          requireChanges={false}
        />
      </form.AppForm>
    </form>
  );
}
